import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahReferenceData } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import {
  SahReferenceDataRefreshResult,
  SahReferenceDataSync,
  SahReferenceDataTag,
} from '~support-at-home/models/sahReferenceData';

// Covers the global Services Australia reference data sync status + manual refresh actions
// (GET /v2/sah/reference-data, POST /v2/sah/reference-data/refresh, POST
// /v2/sah/reference-data/{api_tag}/refresh). Distinct from prodaConnection.ts, which manages the
// PRODA device connection itself rather than the reference lists fetched through it.
export const sahReferenceDataApi = createApi({
  reducerPath: 'sahReferenceDataApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahReferenceData }),
  tagTypes: ['sahReferenceData'],
  endpoints: (builder) => ({
    getSahReferenceData: builder.query<SahReferenceDataSync[], void>({
      query: () => ({ url: '', method: 'GET' }),
      transformResponse: (payload) => unwrap<SahReferenceDataSync[]>(payload),
      providesTags: [{ type: 'sahReferenceData', id: 'LIST' }],
    }),
    // Both refresh mutations invalidate the same list tag as the status query: a refresh call
    // updates last_run/last_status/record_count for one or all rows, so the status table must
    // refetch to show the new sync state rather than the stale pre-refresh snapshot.
    refreshSahReferenceData: builder.mutation<SahReferenceDataRefreshResult, void>({
      query: () => ({ url: '/refresh', method: 'POST' }),
      transformResponse: (payload) => unwrap<SahReferenceDataRefreshResult>(payload),
      invalidatesTags: [{ type: 'sahReferenceData', id: 'LIST' }],
    }),
    refreshSahReferenceDataList: builder.mutation<SahReferenceDataRefreshResult, SahReferenceDataTag>({
      query: (apiTag) => ({ url: `/${apiTag}/refresh`, method: 'POST' }),
      transformResponse: (payload) => unwrap<SahReferenceDataRefreshResult>(payload),
      invalidatesTags: [{ type: 'sahReferenceData', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetSahReferenceDataQuery,
  useRefreshSahReferenceDataMutation,
  useRefreshSahReferenceDataListMutation,
} = sahReferenceDataApi;
